import { createContext, useContext } from "react";
import { ApiError } from "../../api/client";

// ── Shared auditor portal types and context ───────────────────────────────
// GET /auditor/clients is loaded once by the shell (AuditorPortal.tsx) and
// shared with the client list, the calendar, the engagement context block in
// the sidebar and the command palette, so every page sees the same rows and a
// single reload refreshes them all.

export interface BureauSummary {
  id: string;
  name: string;
  accreditation: string | null;
  /** certification_body | competent_authority: a competent authority's audit view carries the Submissions tab. */
  kind?: string;
}

export interface AuditorClientSummary {
  id: string;
  name: string;
  sector: string | null;
  geography: string | null;
  serviceTier: { id: string; level: number; name: string };
  latestCheck: { overallScore: number | null; completedAt: string | null } | null;
  clauseReviewCount: number;
  openFindingsCount: number;
  resolvedFindingsCount: number;
  awaitingVerificationCount: number;
  schedule: { auditType: string; scheduledDate: string; isOverdue: boolean } | null;
  // Enrolled today and within the bureau's accreditation - resolved live by the API.
  frameworkIds: string[];
}

export interface AuditorClientListResponse {
  clients: AuditorClientSummary[];
  bureau: BureauSummary | null;
}

export const AUDIT_TYPE_LABELS: Record<string, string> = {
  initial: "Initial Audit",
  surveillance: "Surveillance Audit",
  recertification: "Recertification Audit",
};

/** The AuditorRole values the API accepts, with their display labels (order = menu order). */
export const AUDITOR_ROLE_LABELS: Record<string, string> = {
  lead_auditor:         "Lead Auditor",
  auditor:              "Auditor",
  technical_auditor:    "Technical Auditor",
  internal_auditor:     "Internal Auditor",
  observer:             "Observer",
  framework_specialist: "Framework Specialist",
};

/** ConsultantRole labels, for consultancy staff listed on Team & Access (read-only rows). */
export const CONSULTANT_ROLE_LABELS: Record<string, string> = {
  lead_consultant:      "Lead Consultant",
  senior_consultant:    "Senior Consultant",
  junior_consultant:    "Junior Consultant",
  framework_specialist: "Framework Specialist",
  report_writer:        "Report Writer",
};

/**
 * How the first administrator of a bureau comes about and how the others are
 * made (docs/NAVIGATION.md section 6). Shown on the Invitations and Team pages.
 */
export const BUREAU_ADMIN_NOTE =
  "The first auditor to join a bureau without an administrator becomes its administrator; administrators can promote other members here.";

/**
 * The sentence for an auditor-invite failure. Both invite forms (the bureau's
 * own page and the platform owner's modal) answer the same codes.
 */
export function submitErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 409 && err.code === "already_member") return "That email address is already a member of a bureau or organisation.";
    if (err.status === 409 && err.code === "invite_pending") return "An invitation for that email address is already pending. Resend or revoke it below.";
    if (err.status === 403) return "Only bureau administrators can send invitations.";
    const payload = err.payload as { error?: unknown } | null;
    if (typeof payload?.error === "string") return payload.error;
    if (err.status === 400) return "Check the name, email address and role, then try again.";
  }
  return "The invitation could not be sent. Please try again.";
}

export interface AuditorClientsState {
  clients: AuditorClientSummary[];
  bureau: BureauSummary | null;
  /** True until the first response (success or failure). */
  loading: boolean;
  error: string;
  /** Re-fetch now; the current rows stay on screen meanwhile. */
  reload: () => void;
}

export const AuditorClientsContext = createContext<AuditorClientsState>({
  clients: [],
  bureau: null,
  loading: true,
  error: "",
  reload: () => {},
});

export function useAuditorClients(): AuditorClientsState {
  return useContext(AuditorClientsContext);
}
